import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Users, Plus, Pencil, Eye, Search, CheckCircle, XCircle, ArrowLeft, ChevronLeft, ChevronRight, Award, ClipboardList, LogIn } from 'lucide-react';
import ProviderDocumentsPage from './ProviderDocumentsPage';
import { useApi } from './DashboardLayout';
import { Link } from 'react-router-dom';

const DF = ({ label, value }) => (
  <div style={{ marginBottom: 12 }}>
    <label className="of-label">{label}</label>
    <div className="of-value">{value || '-'}</div>
  </div>
);

export default function ProvidersPage() {
  const [providers, setProviders] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [selectedIdx, setSelectedIdx] = useState(null);
  const [providerOffers, setProviderOffers] = useState([]);
  const rowRefs = useRef({});
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { apiFetch } = useApi();

  useEffect(() => { loadProviders(); }, []);

  // Auto-select provider from URL param ?view=<id>
  useEffect(() => {
    const viewId = searchParams.get('view');
    if (viewId && providers.length > 0 && selectedIdx === null) {
      const idx = providers.findIndex(p => String(p.id) === viewId);
      if (idx >= 0) selectProvider(idx);
    }
  }, [providers, searchParams]);

  const loadProviders = async () => {
    setLoading(true);
    const r = await apiFetch('/api/providers');
    const d = await r?.json();
    if (d) setProviders(d);
    setLoading(false);
  };

  const toggleStatus = async (p) => {
    const newStatus = p.user_status === 'allow' ? 'block' : 'allow';
    if (newStatus === 'block' && !window.confirm(`"${p.verein}" wirklich sperren?`)) return;
    await apiFetch(`/api/providers/${p.id}/status`, { method: 'POST', body: JSON.stringify({ user_status: newStatus }) });
    loadProviders();
  };

  const impersonateProvider = async (prov) => {
    if (!window.confirm(`Als "${prov.verein}" anmelden?`)) return;
    const r = await apiFetch(`/api/auth/impersonate/${prov.id}`, { method: 'POST' });
    const d = await r?.json();
    if (d?.token) {
      localStorage.setItem('bgpr_original_token', localStorage.getItem('bgpr_token'));
      localStorage.setItem('bgpr_original_user_type', localStorage.getItem('bgpr_user_type'));
      localStorage.setItem('bgpr_original_email', localStorage.getItem('bgpr_email'));
      localStorage.setItem('bgpr_impersonating', d.original_admin_email);
      localStorage.setItem('bgpr_token', d.token);
      localStorage.setItem('bgpr_user_type', d.user_type);
      localStorage.setItem('bgpr_email', d.email || '');
      localStorage.setItem('bgpr_user_id', d.user_id);
      window.location.href = '/app';
    }
  };

  const filtered = providers.filter(p =>
    search === '' || [p.verein, p.ik_number, p.contact_person, p.city_location].some(v => (v || '').toLowerCase().includes(search.toLowerCase()))
  );

  const selectProvider = (idx) => {
    setSelectedIdx(idx);
    const prov = filtered[idx];
    if (prov) {
      apiFetch(`/api/offers?provider_id=${prov.id}`).then(r => r?.json()).then(d => {
        if (Array.isArray(d)) setProviderOffers(d);
      });
    }
  };

  const goBack = () => {
    const lastIdx = selectedIdx;
    setSelectedIdx(null);
    setProviderOffers([]);
    setSearchParams({});
    if (lastIdx !== null) {
      setTimeout(() => {
        const id = filtered[lastIdx]?.id;
        if (id && rowRefs.current[id]) rowRefs.current[id].scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 50);
    }
  };

  const nav = (dir) => {
    const newIdx = selectedIdx + dir;
    if (newIdx >= 0 && newIdx < filtered.length) {
      setSelectedIdx(newIdx);
      const prov = filtered[newIdx];
      if (prov) {
        apiFetch(`/api/offers?provider_id=${prov.id}`).then(r => r?.json()).then(d => {
          if (Array.isArray(d)) setProviderOffers(d);
        });
      }
    }
  };

  const p = selectedIdx !== null ? filtered[selectedIdx] : null;

  // Detail View
  if (p) {
    return (
      <div data-testid="providers-page">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <button className="btn btn-secondary" style={{ padding: '6px 14px', fontSize: 13 }} onClick={goBack} data-testid="provider-back-btn">
              <ArrowLeft size={14} /> Zurück zur Liste
            </button>
            <h2 style={{ margin: 0, fontSize: 20 }}>Anbieter Details</h2>
          </div>
          <div className="detail-nav-pager" data-testid="provider-pager">
            <button className="btn btn-secondary btn-sm" onClick={() => nav(-1)} disabled={selectedIdx === 0} data-testid="provider-prev-btn">
              <ChevronLeft size={14} /> Vorheriger
            </button>
            <span className="detail-nav-count">{selectedIdx + 1} / {filtered.length}</span>
            <button className="btn btn-secondary btn-sm" onClick={() => nav(1)} disabled={selectedIdx === filtered.length - 1} data-testid="provider-next-btn">
              Nächster <ChevronRight size={14} />
            </button>
          </div>
        </div>

        {/* Anbieter header bar */}
        <div className="of-card" style={{ marginBottom: 12, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <label className="of-label">Anbieter</label>
            <div style={{ fontSize: 14, fontWeight: 500 }}>
              {p.verein || '-'}{' '}
              <span style={{ fontFamily: 'monospace', color: 'var(--text-muted)', fontSize: 12 }}>
                IK {p.ik_number || '-'} &middot; Club-ID {p.club_id || '-'}
              </span>
              {' '}&middot;{' '}
              <span style={{ color: p.user_status === 'allow' ? '#10B981' : '#EF4444', fontWeight: 600, fontSize: 12 }}>
                {p.user_status === 'allow' ? 'Aktiv' : 'Gesperrt'}
              </span>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-primary btn-sm" onClick={() => navigate(`/app/providers/${p.id}/edit`)} data-testid="provider-edit-btn">
              <Pencil size={14} /> Bearbeiten
            </button>
            <button className="btn btn-sm btn-secondary" onClick={() => impersonateProvider(p)} data-testid="provider-impersonate-btn" title="Als diesen Anbieter anmelden">
              <LogIn size={14} /> Als Benutzer anmelden
            </button>
            <button className={`btn btn-sm ${p.user_status === 'allow' ? 'btn-secondary' : 'btn-primary'}`} onClick={() => toggleStatus(p)} data-testid="provider-toggle-status">
              {p.user_status === 'allow' ? 'Sperren' : 'Aktivieren'}
            </button>
          </div>
        </div>

        {/* Two column layout: Kontaktdaten + Adresse & Abrechnung */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
          {/* LEFT: Kontaktdaten */}
          <div className="of-card">
            <div className="of-section">Kontaktdaten</div>
            <div className="of-2col">
              <DF label="IK-Nummer" value={p.ik_number} />
              <DF label="Club-ID" value={p.club_id} />
            </div>
            <DF label="Verein" value={p.verein} />
            <DF label="Dachverband" value={p.dachverband} />
            <div className="of-2col">
              <DF label="Ansprechpartner" value={p.contact_person} />
              <DF label="E-Mail" value={p.email} />
            </div>
            <div className="of-2col">
              <DF label="Telefon" value={p.phone} />
              <DF label="Website" value={p.website} />
            </div>
          </div>

          {/* RIGHT: Adresse & Abrechnung */}
          <div className="of-card">
            <div className="of-section">Adresse & Abrechnung</div>
            <DF label="Straße" value={p.street} />
            <div className="of-2col">
              <DF label="PLZ" value={p.postal_code} />
              <DF label="Ort" value={p.city_location} />
            </div>
            <div style={{ marginTop: 8 }} />
            <DF label="Abrechnungsstelle" value={p.billing_office_name} />
            <DF label="Abrechnungsstelle IK" value={p.billing_office_ik_number} />
          </div>
        </div>

        {/* Angebote dieses Anbieters */}
        <div className="of-card">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
            <div className="of-section" style={{ marginBottom: 0 }}>Angebote ({providerOffers.length})</div>
            <Link to={`/app/offers/new?provider_id=${p.id}&from_provider=${p.id}`} style={{ fontSize: 12, fontWeight: 600, color: 'var(--primary)', textDecoration: 'none' }} data-testid="provider-detail-add-offer">
              + Neues Angebot
            </Link>
          </div>
          {providerOffers.length > 0 ? (
            <div className="table-wrap" style={{ margin: '0 -20px -20px', borderTop: 'none' }}>
              <table data-testid="provider-offers-table">
                <thead>
                  <tr>
                    <th style={{ width: 60 }}>Nr.</th>
                    <th>Angebot</th>
                    <th>Veranstaltungsort</th>
                    <th>Zertifizierung</th>
                    <th style={{ width: 100 }}>Aktionen</th>
                  </tr>
                </thead>
                <tbody>
                  {providerOffers.map(o => (
                    <tr key={o.id} data-testid={`provider-offer-row-${o.id}`}
                      onClick={() => navigate(`/app/offers/${o.id}/edit?from_provider=${p.id}`)}
                      style={{ cursor: 'pointer' }}>
                      <td style={{ fontFamily: 'monospace', fontWeight: 600, fontSize: 13 }}>{o.id}</td>
                      <td style={{ fontWeight: 500 }}>{o.angebot || o.offer_name || '-'}</td>
                      <td style={{ color: 'var(--text-secondary)', fontSize: 13 }}>
                        {[o.veranstaltungsort_name, o.veranstaltungsort_strasse, o.veranstaltungsort_plz, o.veranstaltungsort_ort].filter(Boolean).join(', ') || '-'}
                      </td>
                      <td>
                        {o.deregistration_date ? (
                          <span className="badge" style={{ background: '#FEF3C7', color: '#92400E', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                            Abmeldung {o.deregistration_date.split('-').reverse().join('.')}
                          </span>
                        ) : o.certified === 1 ? (
                          <span className="badge badge-success" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                            <Award size={11} /> Zertifiziert
                            {o.cert_valid_until && <span style={{ opacity: 0.7, fontSize: 11, marginLeft: 4 }}>bis {o.cert_valid_until?.substring(0, 10)}</span>}
                          </span>
                        ) : (
                          <span className="badge badge-warning">Nicht zertifiziert</span>
                        )}
                      </td>
                      <td onClick={e => e.stopPropagation()}>
                        <Link to={`/app/offers/${o.id}/edit?from_provider=${p.id}`} className="btn-icon edit" title="Bearbeiten" data-testid={`provider-offer-edit-${o.id}`}>
                          <Pencil size={14} />
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '2rem 0', color: 'var(--text-muted)' }}>
              <ClipboardList size={28} style={{ opacity: 0.3, marginBottom: 8 }} />
              <p style={{ margin: 0, fontSize: 13 }}>Keine Angebote für diesen Anbieter vorhanden.</p>
            </div>
          )}
        </div>

        {/* Dokumente dieses Anbieters */}
        <div className="of-card">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
            <div className="of-section" style={{ marginBottom: 0 }}>Dokumente</div>
            <Link to={`/app/provider-documents/${p.id}`} style={{ fontSize: 12, fontWeight: 600, color: 'var(--primary)', textDecoration: 'none' }} data-testid="provider-detail-documents-link">
              Alle Dokumente anzeigen &rarr;
            </Link>
          </div>
          <ProviderDocumentsPage providerId={p.id} providerName={p.verein} embedded={true} />
        </div>
      </div>
    );
  }

  // List View
  return (
    <div data-testid="providers-page">
      <div className="page-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div><h2>Anbieter</h2><p>{providers.length} Herzgruppenanbieter</p></div>
        <button className="btn btn-primary" onClick={() => navigate('/app/providers/new')} data-testid="add-provider-btn"><Plus size={16} /> Neuer Anbieter</button>
      </div>

      <div className="card">
        <div className="card-body" style={{ padding: '1rem 1.5rem' }}>
          <div className="filter-bar">
            <div className="search-input"><Search size={16} /><input className="form-input" placeholder="Suche nach Verein, IK, Ansprechpartner..." value={search} onChange={e => setSearch(e.target.value)} data-testid="providers-search" /></div>
          </div>
        </div>

        {loading ? <div className="empty-state"><p>Laden...</p></div> : filtered.length === 0 ? (
          <div className="empty-state"><Users size={40} /><p>Keine Anbieter gefunden</p></div>
        ) : (
          <div className="table-wrap">
            <table data-testid="providers-table">
              <thead><tr><th>IK-Nr.</th><th>Verein</th><th>Ort</th><th>Ansprechpartner</th><th>Angebote</th><th>Status</th><th>Aktionen</th></tr></thead>
              <tbody>
                {filtered.map((pr, idx) => (
                  <tr key={pr.id} ref={el => rowRefs.current[pr.id] = el} data-testid={`provider-row-${pr.id}`}
                    onClick={() => selectProvider(idx)} style={{ cursor: 'pointer' }}>
                    <td style={{ fontFamily: 'monospace', fontSize: 13 }}>{pr.ik_number || '-'}</td>
                    <td style={{ fontWeight: 500 }}>{pr.verein || '-'}</td>
                    <td>{pr.city_location || '-'}</td>
                    <td>{pr.contact_person || '-'}</td>
                    <td><span className="badge badge-dark">{pr.offer_count || 0}</span></td>
                    <td>{pr.user_status === 'allow' ? <span className="badge badge-success"><span className="badge-dot green" /> Aktiv</span> : <span className="badge badge-danger"><span className="badge-dot red" /> Gesperrt</span>}</td>
                    <td onClick={e => e.stopPropagation()}>
                      <div className="table-actions">
                        <button className="btn-icon view" onClick={() => selectProvider(idx)} title="Anzeigen" data-testid={`provider-view-${pr.id}`}><Eye size={14} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {!loading && filtered.length > 0 && (
          <div style={{ padding: '0.75rem 1.5rem', borderTop: '1px solid var(--border)', fontSize: 13, color: 'var(--text-secondary)' }}>{filtered.length} von {providers.length} Anbietern</div>
        )}
      </div>
    </div>
  );
}
